/**
 * "New updates available" — the freshest deploy, without a store.
 *
 * A web game has no update prompt of its own; a player who leaves the tab open
 * for days is running whatever build they first loaded. This polls the deployed
 * `version.json` on an interval and, the moment its build id differs from the
 * one baked into the running bundle, floats a button. Tapping it reloads into
 * the new build.
 *
 * `__BUILD_ID__` is replaced at build time (see vite.config). In dev it is the
 * process start time and the fetched file won't exist, so the checker simply
 * stays quiet — exactly what you want while iterating.
 */

const POLL_INTERVAL_MS = 90_000

export class UpdateChecker {
  private readonly button: HTMLButtonElement
  private notified = false

  constructor(parent: HTMLElement) {
    this.button = document.createElement('button')
    this.button.className = 'update-banner'
    this.button.hidden = true
    this.button.innerHTML = '<span class="icon-download-cloud update-banner-icon"></span>New update — tap to reload'
    this.button.addEventListener('click', () => window.location.reload())
    parent.append(this.button)
  }

  /** Begin polling. Also checks when the tab regains focus, for a quick catch. */
  start(): void {
    const check = (): void => void this.check()
    window.setInterval(check, POLL_INTERVAL_MS)
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') check()
    })
    // First check a few seconds in, so a just-shipped build is caught without
    // racing the initial load.
    window.setTimeout(check, 8000)
  }

  private async check(): Promise<void> {
    if (this.notified) return
    try {
      // Cache-bust so a CDN never hands back the build the player already has.
      const response = await fetch(`/version.json?t=${Date.now()}`, { cache: 'no-store' })
      if (!response.ok) return
      // In dev the file doesn't exist and the server answers index.html; only
      // trust a real JSON manifest.
      if (!response.headers.get('content-type')?.includes('json')) return
      const { build } = (await response.json()) as { build?: string }
      if (build && build !== __BUILD_ID__) {
        this.notified = true
        this.button.hidden = false
      }
    } catch {
      // Offline, or no manifest (dev). Try again next tick.
    }
  }
}
